import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, LogIn, Mail, KeyRound, ArrowLeft } from 'lucide-react';
import logo from '../assets/synergynode-logo.png';
import clsLogo from '../assets/logo.png';
import api from '../api/client';

export default function Login() {
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoFlipped, setLogoFlipped] = useState(false);
  const [step, setStep] = useState('login'); // 'login' | 'forgot-email' | 'forgot-otp' | 'forgot-reset'
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotToken, setForgotToken] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [sessionMsg, setSessionMsg] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const reason = sessionStorage.getItem('logout_reason');
    if (reason === 'another_session') {
      setSessionMsg('You were logged out because your account was signed in from another browser or device.');
      sessionStorage.removeItem('logout_reason');
    }
    const flip = setInterval(() => {
      setLogoFlipped(true);
      setTimeout(() => setLogoFlipped(false), 750);
    }, 6000);
    return () => clearInterval(flip);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const data = await login(form.username, form.password);
      navigate('/otp-verify', { state: { userId: data.userId, email: data.email } });
    } catch (err) { setError(err.response?.data?.error || 'Invalid credentials'); }
    finally { setLoading(false); }
  };

  // Forgot: Step 1 - send OTP
  const handleForgotEmail = async e => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email: forgotEmail });
      setSuccessMsg(res.data.message);
      setStep('forgot-otp');
    } catch (err) { setError(err.response?.data?.error || 'Failed to send OTP'); }
    finally { setLoading(false); }
  };

  // Forgot: Step 2 - verify OTP
  const handleForgotOtp = async e => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await api.post('/auth/forgot-verify-otp', { email: forgotEmail, otp: forgotOtp });
      setForgotToken(res.data.resetToken);
      setStep('forgot-reset');
    } catch (err) { setError(err.response?.data?.error || 'Invalid OTP'); }
    finally { setLoading(false); }
  };

  // Forgot: Step 3 - set new password
  const handleForgotReset = async e => {
    e.preventDefault(); setError('');
    if (newPwd !== confirmPwd) return setError('Passwords do not match');
    if (newPwd.length < 8) return setError('Minimum 8 characters');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { email: forgotEmail, token: forgotToken, newPassword: newPwd });
      setStep('login');
      setError('');
      setSuccessMsg('Password reset successful. Please log in.');
    } catch (err) { setError(err.response?.data?.error || 'Reset failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-mesh flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)', top: '10%', left: '15%' }}/>
      <div className="absolute w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)', bottom: '10%', right: '10%' }}/>

      <div className="w-full max-w-sm relative z-10" style={{ animation: 'slideUp 0.5s ease-out' }}>
        {/* Logo — inlined directly, NOT in a sub-component, to preserve CSS animation state */}
        <div className="text-center mb-8">
          <div className="relative mb-5">
            {/* Glow halo behind logo */}
            <div className="absolute inset-0 pointer-events-none rounded-2xl" style={{ background: 'rgba(59,130,246,0.18)', filter: 'blur(36px)', transform: 'scale(1.25)' }}/>
            <img
              src={logo}
              alt="SynergyNode"
              className={logoFlipped ? 'logo-flip' : ''}
              style={{
                width: 280, height: 'auto', objectFit: 'contain', borderRadius: 16,
                background: 'rgba(255,255,255,0.96)', padding: '14px 20px',
                position: 'relative', display: 'block', margin: '0 auto',
                filter: 'drop-shadow(0 0 18px rgba(59,130,246,0.55))',
                border: '1px solid rgba(59,130,246,0.28)', boxShadow: '0 0 48px rgba(59,130,246,0.22)',
              }}
            />
          </div>
          {/* CLS Group attribution */}
          <div className="flex flex-col items-center gap-2 mt-3">
            <p className="text-xs" style={{ color: '#334155' }}>A CL Synergy Group IT Product</p>
            <img src={clsLogo} alt="CL Synergy" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 8, background: 'rgba(255,255,255,0.88)', padding: 4 }}/>
          </div>
        </div>

        <div className="glass-bright rounded-2xl p-7" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 40px rgba(59,130,246,0.08)' }}>

          {/* ANOTHER SESSION NOTICE */}
          {sessionMsg && (
            <div className="mb-4 px-4 py-3 rounded-xl text-sm flex items-start gap-2" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#fbbf24' }}>
              <span className="flex-shrink-0 mt-0.5">⚠</span>
              <span>{sessionMsg}</span>
            </div>
          )}

          {/* SUCCESS MESSAGE */}
          {successMsg && (
            <div className="mb-4 px-4 py-3 rounded-xl text-sm" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', color: '#34d399' }}>
              {successMsg}
            </div>
          )}

          {/* ERROR */}
          {error && (
            <div className="mb-4 px-4 py-3 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171' }}>
              {error}
            </div>
          )}

          {/* ── LOGIN ── */}
          {step === 'login' && (
            <>
              <h2 className="font-semibold mb-5" style={{ color: '#e2e8f0' }}>Sign in to your account</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>Username</label>
                  <input type="text" required value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="Enter your username" className="input-dark"/>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>Password</label>
                  <div className="relative">
                    <input type={showPass ? 'text' : 'password'} required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Enter your password" className="input-dark pr-10"/>
                    <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#475569', background: 'none', border: 'none', cursor: 'pointer' }}>
                      {showPass ? <EyeOff size={15}/> : <Eye size={15}/>}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">
                  <LogIn size={15}/>{loading ? 'Signing in...' : 'Sign In'}
                </button>
                <button type="button" onClick={() => { setStep('forgot-email'); setError(''); setSuccessMsg(''); }} className="w-full text-xs mt-2 transition-colors" style={{ color: '#334155', background: 'none', border: 'none', cursor: 'pointer' }}
                  onMouseEnter={e => e.target.style.color='#60a5fa'} onMouseLeave={e => e.target.style.color='#334155'}>
                  Forgot password?
                </button>
              </form>
            </>
          )}

          {/* ── FORGOT: EMAIL STEP ── */}
          {step === 'forgot-email' && (
            <>
              <div className="flex items-center gap-2 mb-5">
                <button onClick={() => { setStep('login'); setError(''); }} className="btn-ghost p-1"><ArrowLeft size={14}/></button>
                <h2 className="font-semibold" style={{ color: '#e2e8f0' }}>Reset Password</h2>
              </div>
              <p className="text-xs mb-4" style={{ color: '#475569' }}>Enter your registered email address. We'll send a 6-digit OTP.</p>
              <form onSubmit={handleForgotEmail} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>Email Address</label>
                  <input type="email" required value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="your@email.com" className="input-dark"/>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">
                  <Mail size={15}/>{loading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </form>
            </>
          )}

          {/* ── FORGOT: OTP STEP ── */}
          {step === 'forgot-otp' && (
            <>
              <div className="flex items-center gap-2 mb-5">
                <button onClick={() => { setStep('forgot-email'); setError(''); }} className="btn-ghost p-1"><ArrowLeft size={14}/></button>
                <h2 className="font-semibold" style={{ color: '#e2e8f0' }}>Enter OTP</h2>
              </div>
              <p className="text-xs mb-4" style={{ color: '#475569' }}>Enter the 6-digit code sent to <strong style={{ color: '#e2e8f0' }}>{forgotEmail}</strong>. Valid for 10 minutes.</p>
              <form onSubmit={handleForgotOtp} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>6-Digit OTP</label>
                  <input type="text" required maxLength={6} inputMode="numeric" value={forgotOtp} onChange={e => setForgotOtp(e.target.value.replace(/\D/g,''))}
                    placeholder="000000" className="input-dark text-center text-xl tracking-widest" style={{ letterSpacing: 12, fontFamily: 'monospace' }}/>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">
                  <KeyRound size={15}/>{loading ? 'Verifying...' : 'Verify OTP'}
                </button>
                <button type="button" onClick={handleForgotEmail} className="w-full text-xs" style={{ color: '#334155', background: 'none', border: 'none', cursor: 'pointer' }}>Resend OTP</button>
              </form>
            </>
          )}

          {/* ── FORGOT: RESET PASSWORD STEP ── */}
          {step === 'forgot-reset' && (
            <>
              <div className="flex items-center gap-2 mb-5">
                <h2 className="font-semibold" style={{ color: '#e2e8f0' }}>Set New Password</h2>
              </div>
              <form onSubmit={handleForgotReset} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>New Password</label>
                  <input type="password" required value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="Min. 8 characters" className="input-dark"/>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>Confirm Password</label>
                  <input type="password" required value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} className="input-dark"/>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
